import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { FormField } from '../FormField.jsx';
import { TextInput } from '../TextInput.jsx';
import { SubmitButton } from '../SubmitButton.jsx';

export function ProfileSection() {
  const [profile, setProfile] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [fullName, setFullName] = useState('');
  const [fieldError, setFieldError] = useState(null);
  const [globalError, setGlobalError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getSettingsProfile();
        if (cancelled) return;
        setProfile(data);
        setFullName(data.full_name || '');
      } catch (err) {
        if (cancelled) return;
        setLoadError(err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError(null);
    setGlobalError(null);
    setSuccess(false);
    setSaving(true);
    try {
      const trimmed = fullName.trim();
      const res = await api.patchSettingsProfile({ full_name: trimmed });
      setProfile((p) => ({ ...p, full_name: res.full_name }));
      setFullName(res.full_name);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors?.full_name) {
        setFieldError(err.fieldErrors.full_name);
      } else {
        setGlobalError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <section className="settings-section">
        <h2>Profile</h2>
        <p className="invite-error" role="alert">Couldn&rsquo;t load profile.</p>
      </section>
    );
  }

  if (!profile) {
    return (
      <section className="settings-section">
        <h2>Profile</h2>
        <p className="form-loading">Loading…</p>
      </section>
    );
  }

  return (
    <section className="settings-section">
      <h2>Profile</h2>
      <p className="settings-section-hint">Update the name your team and reports show.</p>

      <form onSubmit={handleSubmit} className="settings-form" noValidate>
        <FormField label="Full name" htmlFor="profile-full-name" error={fieldError}>
          <TextInput
            id="profile-full-name"
            name="full_name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            error={Boolean(fieldError)}
            maxLength={80}
            autoComplete="name"
            required
          />
        </FormField>

        <FormField label="Email" htmlFor="profile-email" hint="Email is fixed for this account.">
          <TextInput
            id="profile-email"
            name="email"
            type="email"
            value={profile.email}
            readOnly
            disabled
            autoComplete="email"
          />
        </FormField>

        <div className="settings-form-actions">
          <SubmitButton loading={saving} disabled={!fullName.trim()}>
            Save profile
          </SubmitButton>
          {success && (
            <span className="invite-success" role="status">Profile updated.</span>
          )}
          {globalError && (
            <span className="invite-error" role="alert">
              {globalError instanceof ApiError ? (globalError.message || 'Couldn\u2019t update profile.') : 'Couldn\u2019t update profile.'}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}
